import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Spline } from 'lucide-react';
import { usePluginStore } from '@/stores/plugin-store';
import { useTrajectoryStore, LOOKAHEAD_OPTIONS, type LookaheadMinutes } from '@/stores/trajectory-store';
import { buildActiveMapLayers } from '@/components/map/plugin-layer-manager';
import { cn } from '@/lib/utils';

/** A trajectory-capable layer shown in the control */
interface TrajectoryLayer {
  id: string;
  label: string;
}

/** Trajectory-capable feed layers from the active plugin manifest */
function trajectoryLayers(): TrajectoryLayer[] {
  return buildActiveMapLayers(usePluginStore.getState().activePlugins)
    .filter((layer) => layer.definition.supportsTrajectory)
    .map((layer) => ({ id: layer.fullId, label: layer.definition.label }));
}

/** HUD control for predicted-trajectory (velocity-vector) rendering. */
export function TrajectoryControl(): React.JSX.Element | null {
  const activePlugins = usePluginStore((s) => s.activePlugins);
  const enabledCount = useTrajectoryStore((s) => s.enabledLayers.size);
  const [open, setOpen] = useState(false);

  const layers = trajectoryLayers();
  void activePlugins; // re-render when the manifest changes
  if (layers.length === 0) return null;

  return (
    <div className="flex flex-col items-end">
      <ChipButton active={enabledCount > 0} count={enabledCount} onClick={() => setOpen((v) => !v)} />
      <AnimatePresence>{open && <TrajectoryPanel layers={layers} />}</AnimatePresence>
    </div>
  );
}

/** Collapsed chip toggling the panel */
function ChipButton({
  active,
  count,
  onClick,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      onClick={onClick}
      title="Predicted trajectories"
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-md border border-echo-border bg-[#1a1a1a]/90 px-2.5 text-xs font-medium backdrop-blur-sm transition-colors',
        active ? 'text-echo-yellow' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <Spline className="h-4 w-4" />
      <span>Vectors{count > 0 ? ` (${count})` : ''}</span>
    </button>
  );
}

/** Expanded panel: look-ahead selector + per-layer toggles */
function TrajectoryPanel({ layers }: { layers: TrajectoryLayer[] }): React.JSX.Element {
  const lookahead = useTrajectoryStore((s) => s.lookaheadMinutes);
  const setLookahead = useTrajectoryStore((s) => s.setLookahead);

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.12 }}
      className="mt-1.5 w-52 rounded-md border border-echo-border bg-[#1a1a1a]/95 p-2.5 backdrop-blur-sm"
    >
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Look-ahead</p>
      <div className="mb-2.5 flex gap-1">
        {LOOKAHEAD_OPTIONS.map((minutes) => (
          <LookaheadButton
            key={minutes}
            minutes={minutes}
            active={lookahead === minutes}
            onClick={() => setLookahead(minutes)}
          />
        ))}
      </div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Layers</p>
      <div className="flex flex-col gap-1">
        {layers.map((layer) => (
          <LayerToggle key={layer.id} layer={layer} />
        ))}
      </div>
    </motion.div>
  );
}

/** A single look-ahead segmented option */
function LookaheadButton({
  minutes,
  active,
  onClick,
}: {
  minutes: LookaheadMinutes;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 rounded px-2 py-1 text-xs font-semibold transition-colors',
        active
          ? 'bg-echo-yellow/15 text-echo-yellow'
          : 'bg-white/[0.04] text-muted-foreground hover:text-foreground',
      )}
    >
      {minutes}m
    </button>
  );
}

/** A per-layer trajectory toggle row */
function LayerToggle({ layer }: { layer: TrajectoryLayer }): React.JSX.Element {
  const enabled = useTrajectoryStore((s) => s.enabledLayers.has(layer.id));
  const toggleLayer = useTrajectoryStore((s) => s.toggleLayer);

  return (
    <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground">
      <input
        type="checkbox"
        checked={enabled}
        onChange={() => toggleLayer(layer.id)}
        className="h-3.5 w-3.5 accent-echo-yellow"
      />
      <span className="truncate">{layer.label}</span>
    </label>
  );
}
